import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect } from "react";
import styles from "./standard-token-window.module.scss";
import { getERC20TokenBalances } from "@/features/bridge/services/api/getTokenBalances";
import { useStandardTokenManager } from "@/features/bridge/stores/standard-token-manager.store";
import { useTokenWindowControls } from "@/features/bridge/stores/token-window.store";
import { CheckoutStatusType } from "@/features/bridge/types/checkout.types";
import {
  TokenType,
  type StandardTokenType,
} from "@/features/bridge/types/token-selector.types";
import ModalPopper from "@components/layout/modal-popper/modal-popper";
import Toolbar from "@/features/bridge/components/token-selector/selector-interface/toolbar";
import StandardTokenList from "@/features/bridge/components/token-selector/selector-interface/standard-token/standard-token-list";
import BlessButton from "@components/layout/button/green-outline-btn";
import CheckoutLoader from "@/features/bridge/components/token-confirmation/checkout/checkout-loader";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import BridgingHeader from "../bridging-header";
import { useBlessnetValidation } from "@/features/bridge/hooks/useBlessnetValidation.hook";
import BlessnetUnavailableModal from "@/components/layout/modal/blessnet-unavailable-modal";

export interface Token {
  id: string;
  name: string;
  image: string;
  collection: string;
}
const StandardTokenWindow = () => {
  const {
    bridgeControls: {
      fromChain: { originChain },
      toChain: { destinationChain },
      isBridgePending,
      checkoutLoadingStatus,
      setCheckoutLoadingStatus,
      account,
    },
    contractManager: {
      bridgeTokens,
      generalAssets: { checkoutForERC20 },
    },
  } = useBridge();

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["token_balances", originChain?.chainId, account.address],
    queryFn: () => {
      clearTokenSelection();
      return getERC20TokenBalances(account.address, originChain?.chainId);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!originChain?.chainId && !!account.address,
  });

  useEffect(() => {
    query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.address, query.refetch]);

  const standardToken = useStandardTokenManager((state) => ({
    token: state.selectedToken,
    tokenAmount: state.tokenAmount,
  }));

  const clearTokenSelection = useStandardTokenManager(
    (state) => state.clearStandardTokenState,
  );

  const { activeTokenWindowType, setActiveTokenWindowType } =
    useTokenWindowControls();
  const isOpen = activeTokenWindowType === TokenType.STANDARD;

  // Blessnet validation hook
  const {
    validateBlessnetBridging,
    isBridgingToBlessnet,
    isValidating,
    showUnavailableModal,
    closeUnavailableModal,
  } = useBlessnetValidation();

  const handleConfirm = async () => {
    // BLESSNET SAFETY CHECK
    if (isBridgingToBlessnet(destinationChain)) {
      console.log('🔐 [STANDARD TOKEN] Performing Blessnet safety check...');

      const tokenAddress = standardToken.token?.tokenAddress;

      const isValidForBlessnet = await validateBlessnetBridging(
        destinationChain,
        tokenAddress,
        CONTRACT_ADDRESSES.SHIFT_GATEWAY,
        originChain?.chainId
      );

      if (!isValidForBlessnet) {
        console.log('❌ [STANDARD TOKEN] Blessnet validation failed - blocking');
        return; // Modal will be shown by the hook
      }

      console.log('✅ [STANDARD TOKEN] Blessnet validation passed');
    }

    //? If the token is bless handle it this way
    let isSuccess;

    // Store token info before clearing for optimistic update
    const tokenToUpdate = {
      address: standardToken.token?.tokenAddress,
      amount: standardToken.tokenAmount,
      decimals: standardToken.token?.decimals,
    };

    // Clear the selection immediately to prevent double-clicking and allow new selections
    clearTokenSelection();

    // Remove all status updates and replace the state in the component with the isBridgePending
    if (tokenToUpdate.address === CONTRACT_ADDRESSES.BLESS_TOKEN) {
      // setCheckoutLoadingStatus(CheckoutStatusType.BridgingOut);
      isSuccess = await bridgeTokens(tokenToUpdate.amount.toString());
      // setCheckoutLoadingStatus(CheckoutStatusType.Success);
    } else {
      isSuccess = await checkoutForERC20(
        destinationChain,
        tokenToUpdate.address,
        tokenToUpdate.amount,
        tokenToUpdate.decimals,
      );
    }
    if (isSuccess) {
      // setCheckoutLoadingStatus(CheckoutStatusType.Success);

      //? Optimistically removes the sent tokens from the selected tokens available amount
      queryClient.setQueryData(
        ["token_balances", originChain?.chainId],
        produce((draft: StandardTokenType[] | undefined) => {
          if (!draft) return;
          const tokenToChange = draft.find(
            (token) =>
              token.tokenAddress === tokenToUpdate.address,
          );
          if (tokenToChange) {
            tokenToChange.balance -= tokenToUpdate.amount;
          }
        }),
      );
      setTimeout(() => {}, 3000);
    }
    //  else {
    //   setCheckoutLoadingStatus(null);
    // }
  };

  //? When clicking close button also set checkout loading status to null
  // const handleOnSuccessClose = () => {
  //   setCheckoutLoadingStatus(null);
  // };

  //? Only set the checkout status to null if successful, otherwise let it complete its cycle
  const handleWindowClose = () => {
    if (checkoutLoadingStatus === CheckoutStatusType.Success) {
      setActiveTokenWindowType(null);
      // setCheckoutLoadingStatus(null);
    } else {
      setActiveTokenWindowType(null);
    }
  };

  const handleClear = () => {
    clearTokenSelection();
  };

  const handleRefetch = () => query.refetch();

  return (
    <ModalPopper isOpen={isOpen} onClose={handleWindowClose} destroyOnClose>
      <div className={styles.container}>
        <div className={styles.selectTokenHeader}>
          <BridgingHeader
            originChain={originChain}
            destinationChain={destinationChain}
          />
        </div>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <Toolbar
              refetchTokens={handleRefetch}
              showClearBtn
              onClear={handleClear}
            />
            <StandardTokenList query={query} />
          </div>
          <div className={styles.bottomSection}>
            <BlessButton
              disabled={
                !!!standardToken.token ||
                query.isLoading ||
                isValidating
              }
              onClick={handleConfirm}
            >
              {isValidating ? "Validating..." : "Bridge Now"}
            </BlessButton>
          </div>
        </div>
      </div>
      <CheckoutLoader
        loadingStatus={isBridgePending ? CheckoutStatusType.CheckingOut : null}
        // buttonFunc={handleOnSuccessClose}
        buttonText="Close"
      />

      {/* Blessnet Unavailable Modal */}
      <BlessnetUnavailableModal
        isOpen={showUnavailableModal}
        onClose={closeUnavailableModal}
      />
    </ModalPopper>
  );
};

export default StandardTokenWindow;
